import { Button, Col, Row, Text } from '@app/components'
import { cryptoCurrencies, currenciesWithFlags } from '@app/data'
import { BuyAndSellCurrencyInputProps } from '@app/types'
import { ETHEREUM_MAINNET_ID, useWalletChainId } from 'libs/ethereum/useWalletChainId'
import { Dispatch, SetStateAction } from 'react'

import { AmountInput, AmountRow, FieldStack, LabelRow, TokenLabel, TokenSelect, TokenStatic } from './elements'

const PULSE_RECEIVE_CODES = ['PLS', 'PLSX', 'HEX', 'INC', 'PHLPv2', 'CST', 'PRVX', 'GLORY', 'TIDE', 'WAIT']

const CryptoSelect = ({
  type,
  setType,
  ehexOnly,
}: {
  type: string
  setType: Dispatch<SetStateAction<string>>
  ehexOnly: boolean
}) => {
  const options = (ehexOnly ? ['eHEX'] : PULSE_RECEIVE_CODES)
    .map((code) => cryptoCurrencies.find((i) => i.code === code))
    .filter(Boolean)
    .map((currency) => ({
      value: currency.code,
      label: <TokenLabel flag={currency.flag} code={currency.code} isCrypto size="menu" />,
      selected: <TokenLabel flag={currency.flag} code={currency.code} isCrypto size="field" />,
    }))

  return (
    <TokenSelect value={type} onSelect={(v) => setType(v as string)} optionLabelProp="selected" options={options} />
  )
}

export const CurrencyInput: React.FC<BuyAndSellCurrencyInputProps> = ({ pay, receive, setAmount, balance }) => {
  const chainId = useWalletChainId()
  const onEthereum = chainId === ETHEREUM_MAINNET_ID

  const usd = currenciesWithFlags.find((c) => c.code === 'USD') ?? currenciesWithFlags[0]

  const usdBalance = (Number.isFinite(Number(balance)) ? Number(balance) : 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  const getInputDropdowns = (input) => {
    return (
      <FieldStack>
        <LabelRow>
          <Text>{input.title}</Text>
          {input.balanceMax || <span />}
        </LabelRow>
        <AmountRow>
          <AmountInput
            readOnly={input.option == 'You Receive'}
            min={0}
            placeholder={'Enter Amount'}
            value={input.option == 'You Receive' && input.state.amount == 'Loading' ? 'Loading...' : input.state.amount}
            onChange={(v) => setAmount(v as number, input.option)}
          />
          {input.options}
        </AmountRow>
      </FieldStack>
    )
  }

  const currencyListOptions = [
    {
      title: 'You Pay',
      balanceMax: (
        <Text style={{ whiteSpace: 'nowrap' }}>
          (${usdBalance}{' '}
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => setAmount(balance, 'You Pay')}
          >
            Max
          </Button>
          )
        </Text>
      ),
      options: (
        <TokenStatic>
          <TokenLabel flag={usd.flag} code="USD" isCrypto={false} />
        </TokenStatic>
      ),
      state: pay,
      option: 'You Pay',
    },
    {
      title: 'You Receive',
      options: <CryptoSelect type={receive.type} setType={receive.setType} ehexOnly={onEthereum} />,
      state: receive,
      option: 'You Receive',
    },
  ]

  return (
    <Row>
      {currencyListOptions.map((input, index) => (
        <Col span={24} key={index}>
          {getInputDropdowns(input)}
        </Col>
      ))}
    </Row>
  )
}
